import { autoBind } from "src/shared/decorators/autobind";
import type { MedicineDeliveryDocument } from "src/domains/medicine-delivery/models/document";
import { map } from "rxjs/operators";
import { Clinic } from "src/shared/models/clinic.model";
import {
    DeliveryBasicData,
    IMedicineDeliveryService,
    IMedicineDeliverytRepository,
    MedicineDeliveryRequestIdResponse,
} from "src/domains/medicine-delivery/types";
import { MedicineDeliveryDocumentMapper } from "src/domains/medicine-delivery/mappings/document";
import type { AttentionType } from "src/domains/medicine-delivery/presentation/pages/basic-data/types";
import { ClinicMapping } from "src/domains/medicine-delivery/mappings/clinic";
import { MedicineDeliveryRequest } from "src/domains/medicine-delivery/models/request";
import { MedicineDeliveryRequestMapping } from "src/domains/medicine-delivery/mappings/request";

export class MedicineDeliveryService implements IMedicineDeliveryService {
    constructor(private repo: IMedicineDeliverytRepository) {}

    @autoBind
    getRequestId(
        data: Partial<DeliveryBasicData>,
        systemCode: string
    ): Promise<MedicineDeliveryRequestIdResponse> {
        return this.repo.getRequestId(data, systemCode).toPromise();
    }

    @autoBind
    getClinics(
        clientType: string,
        attentionType: AttentionType
    ): Promise<Clinic[]> {
        return this.repo
            .getClinics(clientType, attentionType)
            .pipe(
                map(ClinicMapping.fromJsonArray),
                map((clinics) =>
                    clinics.map((clinic) => {
                        clinic.name = clinic.name.toUpperCase();
                        return clinic;
                    })
                )
            )
            .toPromise();
    }

    @autoBind
    getActiveRequests(): Promise<MedicineDeliveryRequest[]> {
        return this.repo
            .getActiveRequests()
            .pipe(map(MedicineDeliveryRequestMapping.fromJsonArray))
            .toPromise();
    }

    @autoBind
    getDocuments(requestId: number): Promise<MedicineDeliveryDocument[]> {
        return this.repo
            .getDocuments(requestId)
            .pipe(map(MedicineDeliveryDocumentMapper.fromJsons))
            .toPromise();
    }

    @autoBind
    uploadDocument(
        requestId: number,
        file: File
    ): Promise<MedicineDeliveryDocument> {
        return this.repo
            .uploadDocument(requestId, file)
            .pipe(map(MedicineDeliveryDocumentMapper.fromJson))
            .toPromise();
    }

    @autoBind
    removeDocument(requestId: number, fileId: number): Promise<void> {
        return this.repo.removeDocument(requestId, fileId).toPromise();
    }
}
